import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import { Ship } from "../../../types";
import { toast } from "react-toastify";
import { fetchShips, deleteShips, createShip, updateShip } from "./shipAsyncThunk";
import { IAuthResponse } from "../auth/IAuthState";
import { RequestResults } from "../../common/RequestStatus";

export interface DeleteShips extends IAuthResponse {
  id: string;
}

interface ShipState {
  data: Ship[];
  status: RequestResults;
  loading: boolean;
  error: string | null;
}

const initialState: ShipState = {
  data: [],
  status: RequestResults.idle,
  loading: false,
  error: null,
};

// Slice
const shipSlice = createSlice({
  name: "ships",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchShips.pending, (state) => {
        state.status = RequestResults.pending;
        state.error = null;
      })
      .addCase(fetchShips.fulfilled, (state, action: PayloadAction<Ship[]>) => {
        state.status = RequestResults.success;
        state.data = action.payload;
      })
      .addCase(fetchShips.rejected, (state, action) => {
        state.status = RequestResults.failed;
        toast.error(action.payload || "Failed to fetch ships");
        state.error = action.payload || "Failed to fetch ships";
      });
    builder
      .addCase(createShip.pending, (state) => {
        state.loading = true;
      })
      .addCase(createShip.fulfilled, (state, action: PayloadAction<Ship>) => {
        state.loading = false;
        state.data.push(action.payload);
      })
      .addCase(createShip.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload || "Failed to add Ship");
        state.error = action.payload || "Failed to add Ship";
      });
    builder
      .addCase(updateShip.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateShip.fulfilled, (state, action: PayloadAction<Ship>) => {
        const index = state.data.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.data[index] = action.payload;
        }
        state.loading = false;
      })
      .addCase(updateShip.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload || "Failed to update Ship");
        state.error = action.payload || "Failed to update Ship";
      });
    builder
      .addCase(deleteShips.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteShips.fulfilled, (state, action: PayloadAction<DeleteShips>) => {
        const index = state.data.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.data = state.data.filter((item) => item.id !== action.payload.id);
        }
        state.loading = false;
      })
      .addCase(deleteShips.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload || "Failed to delete Ship");
        state.error = action.payload || "Failed to delete Ship";
      });
  },
});

export const selectShips = (state: RootState) => state.ships;

export default shipSlice.reducer;
